import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Warehouse, Loader2, FileText, Clock, CheckCircle2, TrendingUp, TrendingDown,
  ArrowRight, Package, Building2,
} from 'lucide-react';
import { seasonsAPI, commoditiesAPI, godownsAPI, storageBillsAPI } from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  submitted: 'bg-blue-100 text-blue-800 border-blue-300',
  dm_verified: 'bg-orange-100 text-orange-800 border-orange-300',
  manager_approved: 'bg-green-100 text-green-800 border-green-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
};
const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  dm_verified: 'DM Verified',
  manager_approved: 'Approved',
  rejected: 'Rejected',
};
const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Summary = {
  byStatus: Record<string, number>;
  totals: { total_bills: number; total_payable: number; approved_amount: number; pending_action: number };
  byMonth: { year: number; month: number; amount: number; count: number }[];
  byGodown: { godown_id: number; name: string; amount: number; count: number }[];
  byCommodity: { commodity_id: number; name: string; amount: number; count: number }[];
  prevSeason: { season_id: number; total_bills: number; total_payable: number; approved_amount: number } | null;
};

export const StorageSummary: React.FC = () => {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [godowns, setGodowns] = useState<any[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [godownFilter, setGodownFilter] = useState<number>(0);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, g] = await Promise.all([seasonsAPI.list(), commoditiesAPI.list(), godownsAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setCommodities(Array.isArray(c.data) ? c.data : []);
        setGodowns(Array.isArray(g.data) ? g.data : []);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
        else setLoading(false);
      } catch {
        setLoading(false);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!selectedSeasonId) return;
    setLoading(true);
    try {
      const params: any = {};
      if (commodityFilter) params.commodity_id = commodityFilter;
      if (godownFilter) params.godown_id = godownFilter;
      const res = await storageBillsAPI.summary(selectedSeasonId, params);
      setSummary(res.data as Summary);
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedSeasonId, commodityFilter, godownFilter]);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  const s = summary || { byStatus: {}, totals: { total_bills: 0, total_payable: 0, approved_amount: 0, pending_action: 0 }, byMonth: [], byGodown: [], byCommodity: [], prevSeason: null };

  const yoyDelta = s.prevSeason ? s.totals.total_payable - s.prevSeason.total_payable : 0;
  const yoyPct = s.prevSeason && s.prevSeason.total_payable > 0
    ? ((yoyDelta / s.prevSeason.total_payable) * 100)
    : 0;

  const prevSeasonName = s.prevSeason
    ? seasons.find((x) => x.id === s.prevSeason!.season_id)?.season_name || `Season #${s.prevSeason.season_id}`
    : null;

  const maxMonthAmount = Math.max(1, ...s.byMonth.map((m) => m.amount));
  const maxGodownAmount = Math.max(1, ...s.byGodown.map((g) => g.amount));
  const totalCommodityAmount = Math.max(1, s.byCommodity.reduce((sum, c) => sum + c.amount, 0));

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Warehouse className="w-7 h-7 text-indigo-600" />
            Storage Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Monthly warehouse storage charges — overview and trends</p>
        </div>
        <button onClick={() => navigate('/storage/bills')}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg text-sm">
          All Bills <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {seasons.map((x) => (<option key={x.id} value={x.id}>{x.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Commodity</label>
            <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Godown</label>
            <select value={godownFilter} onChange={(e) => setGodownFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All</option>
              {godowns.map((g) => (<option key={g.id} value={g.id}>{g.godown_name}</option>))}
            </select>
          </div>
        </div>
      </div>

      {/* YoY banner */}
      {s.prevSeason && (
        <div className="mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-4 flex-wrap">
          {yoyDelta >= 0
            ? <TrendingUp className="w-7 h-7 text-orange-600" />
            : <TrendingDown className="w-7 h-7 text-green-600" />}
          <div className="flex-1">
            <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">vs. {prevSeasonName}</div>
            <div className="text-sm text-gray-700 mt-0.5">
              {s.prevSeason.total_bills} bills · {formatAmount(s.prevSeason.total_payable)}
              <span className="mx-2 text-gray-300">→</span>
              {s.totals.total_bills} bills · {formatAmount(s.totals.total_payable)}
            </div>
          </div>
          <div className={`text-right ${yoyDelta >= 0 ? 'text-orange-700' : 'text-green-700'}`}>
            <div className="text-lg font-bold">
              {yoyDelta >= 0 ? '+' : ''}{formatAmount(yoyDelta)}
            </div>
            <div className="text-xs">
              {yoyDelta >= 0 ? '+' : ''}{yoyPct.toFixed(1)}%
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={<FileText className="w-5 h-5" />} label="Total Bills" value={String(s.totals.total_bills)} sub="this season" tint="indigo" />
        <StatCard icon={<Clock className="w-5 h-5" />} label="Pending Action" value={String(s.totals.pending_action)} sub="awaiting verify/approve" tint="orange" />
        <StatCard icon={<CheckCircle2 className="w-5 h-5" />} label="Approved Amount" value={formatAmount(s.totals.approved_amount)} sub={`${s.byStatus.manager_approved || 0} approved`} tint="green" />
        <StatCard icon={<TrendingUp className="w-5 h-5" />} label="Total Payable" value={formatAmount(s.totals.total_payable)} sub="all statuses" tint="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Status breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Status Breakdown</h3>
          <div className="space-y-2">
            {(['draft', 'submitted', 'dm_verified', 'manager_approved', 'rejected'] as const).map((st) => {
              const count = num(s.byStatus[st]);
              const pct = s.totals.total_bills > 0 ? Math.round((count / s.totals.total_bills) * 100) : 0;
              return (
                <div key={st} className="flex items-center gap-3 text-xs">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_COLORS[st]} min-w-[110px] justify-center`}>{STATUS_LABEL[st]}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div className={`h-full ${STATUS_COLORS[st].split(' ')[0].replace('100', '400')}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-gray-700 font-mono font-semibold min-w-[3ch] text-right">{count}</span>
                  <span className="text-gray-400 text-[10px] min-w-[3ch] text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly trend */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Monthly Spend</h3>
          {s.byMonth.length === 0 ? (
            <div className="text-xs text-gray-400 py-8 text-center">No monthly data yet.</div>
          ) : (
            <div className="space-y-2">
              {s.byMonth.slice(-6).map((m) => {
                const label = `${MONTH_NAMES[m.month]} ${m.year}`;
                const pct = Math.round((m.amount / maxMonthAmount) * 100);
                return (
                  <div key={`${m.year}-${m.month}`} className="flex items-center gap-3 text-xs">
                    <span className="text-gray-600 font-medium min-w-[70px]">{label}</span>
                    <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden relative">
                      <div className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600" style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold text-gray-800">{formatAmount(m.amount)}</span>
                    </div>
                    <span className="text-gray-400 text-[10px] min-w-[40px] text-right">{m.count} bills</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* By commodity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Package className="w-4 h-4 text-indigo-600" />
            By Commodity
          </h3>
          {s.byCommodity.length === 0 ? (
            <div className="text-xs text-gray-400 py-8 text-center">No commodity data.</div>
          ) : (
            <div className="space-y-2">
              {s.byCommodity.map((c) => {
                const pct = Math.round((c.amount / totalCommodityAmount) * 100);
                return (
                  <div key={c.commodity_id} className="flex items-center gap-3 text-xs">
                    <span className="text-gray-700 font-medium min-w-[120px] truncate" title={c.name}>{c.name}</span>
                    <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden relative">
                      <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600" style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold text-gray-800">{formatAmount(c.amount)}</span>
                    </div>
                    <span className="text-gray-400 text-[10px] min-w-[35px] text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top godowns */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-600" />
            Top Godowns by Spend
          </h3>
          {s.byGodown.length === 0 ? (
            <div className="text-xs text-gray-400 py-8 text-center">No godown data.</div>
          ) : (
            <div className="space-y-2">
              {s.byGodown.slice(0, 5).map((g) => {
                const pct = Math.round((g.amount / maxGodownAmount) * 100);
                return (
                  <div key={g.godown_id} className="flex items-center gap-3 text-xs">
                    <span className="text-gray-700 font-medium min-w-[120px] truncate" title={g.name}>{g.name}</span>
                    <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden relative">
                      <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold text-gray-800">{formatAmount(g.amount)}</span>
                    </div>
                    <span className="text-gray-400 text-[10px] min-w-[40px] text-right">{g.count} bills</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tint: 'indigo' | 'orange' | 'green' | 'blue';
}> = ({ icon, label, value, sub, tint }) => {
  const tintCls = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  }[tint];
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border ${tintCls}`}>{icon}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-xs text-gray-400 mt-1">{sub}</div>
    </div>
  );
};

export default StorageSummary;
